import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CategoryIcon from "@mui/icons-material/Category";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import TranslateIcon from "@mui/icons-material/Translate";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  FormHelperText,
  Grid,
  MenuItem,
  Stack,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import axios from "axios";
import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { APP_IMAGES } from "../../assets";
import globalStyle from "../../globalStyle";
import { baseUrl } from "../../services";
import {
  createServiceSubCategory,
  updateServiceSubCategory,
} from "../../services/serviceSubCategoryApiFn";
import { capitalizeText, decodeHtml, handleOpenSnackBar } from "../../utils";
import { translateText } from "../../utils/translationService";
import { serviceSubCategoryValidation } from "../../utils/Validation";
import AppLoader from "../AppLoader";
import GlobalButton from "../CustomButton";
import CustomDropDown from "../CutomDropDown";
import DialogBody from "../Dialog/Body";
import DialogActionButtons from "../Dialog/Footer";
import DialogHeader from "../Dialog/Header";
import KeywordsTagInput from "../KeywordsTagInput";
import Label from "../Label";
import RichTextEditor from "../TextEditor";
import TextInput from "../TextInputs";
import UploadBox from "../UploadBox";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(4, 2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
  "& .MuiInputBase-multiline": {
    padding: "6px !important",
  },
}));

const serviceType = [
  {
    value: "Room",
    type: "room",
  },
  {
    value: "Area",
    type: "area",
  },
  {
    value: "Fixed",
    type: "fixed",
  },
  {
    value: "Room + Area",
    type: "room+area",
  },
  {
    value: "Interior + Exterior",
    type: "interior+exterior",
  },
];

const initialInputValues = {
  categoryId: "",
  image: null,
  smallIcon: null,
  sortingNum: 1,
  type: "",
  name_en: "",
  name_ro: "",
  description_en: "",
  description_ro: "",
  metaTitle_en: "",
  metaTitle_ro: "",
  metaDescription_en: "",
  metaDescription_ro: "",
  metaKeywords_en: [],
  metaKeywords_ro: [],
  profitPer: 0,
  duration: 0,
  longDescription_en: "",
  longDescription_ro: "",
};

const useStyle = {
  customDropDown: {
    "& .MuiSelect-outlined": {
      textTransform: "capitalize",
      padding: "12px 14px",
    },
  },
};

const AddEditServiceSubCategoryDialog = forwardRef(({ }, ref) => {
  const openImageFile = useRef(null);
  const openIconFile = useRef(null);
  const [open, setOpen] = useState(false);
  const { t } = useTranslation(["catalogue", "common"]);
  const [dialogData, setDialogData] = useState(null);
  const [isLoading, setIsLoading] = useState({
    image: false,
    smallIcon: false,
    save: false,
    translateToEnglish: false,
  });
  const [questions, setQuestions] = useState([]);
  const [duration, setDuration] = useState({});
  const [questionVal, setQuestionVal] = useState("");
  const [qsType, setQsType] = useState("add");
  const [qsIndex, setQsIndex] = useState(0);
  const [inputError, setInputError] = useState({});
  const [inputValues, setInputValues] = useState(initialInputValues);
  const [isDisabled, setIsDisabled] = useState(false);

  const serviceCategoryId = useSelector(
    (state) => state.detailIdReducer.detailIds.serviceCategoryId,
  );
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      setDialogData(params);
      setIsDisabled(params.type === "edit" ? true : false);
      if (params.type === "edit") {
        const data = params.data;
        setInputValues({
          image: data?.image || null,
          smallIcon: data?.activeIcon || null,
          type: data?.type || "",
          sortingNum: data?.serialNo || 1,
          name_en: capitalizeText(data?.name?.en || ""),
          name_ro: capitalizeText(data?.name?.ro || ""),
          description_en: data?.description?.en || "",
          description_ro: data?.description?.ro || "",
          metaTitle_en: capitalizeText(data?.metaTitle?.en || ""),
          metaTitle_ro: capitalizeText(data?.metaTitle?.ro || ""),
          metaDescription_en: data?.metaDescription?.en || "",
          metaDescription_ro: data?.metaDescription?.ro || "",
          metaKeywords_en: data?.metaKeywords?.en || [],
          metaKeywords_ro: data?.metaKeywords?.ro || [],
          profitPer: data?.percentage || 0,
          longDescription_en: decodeHtml(data?.longDescription?.en || ""),
          longDescription_ro: decodeHtml(data?.longDescription?.ro || ""),
        });
        setQuestions(data?.clientQuestionnaire || []);
        setDuration(data?.duration || {});
      }
    },
    closeDialog() {
      handleClose();
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputValues(initialInputValues);
    setInputError({});
    setQuestions([]);
    setDuration({});
  };

  const handleUploadImage = async (event, type) => {
    try {
      setIsLoading((prev) => ({ ...prev, [type]: true }));
      const formData = new FormData();
      formData.append("image", event.target.files[0]);
      const response = await axios({
        method: "post",
        url: `${baseUrl}image/upload`,
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      setInputError((pre) => ({ ...pre, [type]: false }));
      setInputValues((pre) => ({ ...pre, [type]: response?.data?.url }));

      handleOpenSnackBar("success", t("success.imageUploadSuccess", { ns: "common" }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.imageUploadFail", { ns: "common" }));
    } finally {
      openImageFile.current.value = null;
      openIconFile.current.value = null;
      setIsLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleChageInputs = (e) => {
    const { name, value, valueAsNumber, type } = e.target;
    const formatedValue = type === "number" ? valueAsNumber : value;

    if (name === "profitPer") {
      if (formatedValue < 0 || formatedValue > 100) {
        setInputError((pre) => ({
          ...pre,
          profitPer: t("validation.profitPercentageMustBeBetween0And100", { ns: "common" })
        }));
        return;
      }
    }

    setInputValues((pre) => ({ ...pre, [name]: formatedValue }));
    setInputError((pre) => ({
      ...pre, [name]: "",
      ...(name === "type" && {
        area: "",
        room: "",
        fixed: "",
        interior: "",
        exterior: "",
      }),
    }));
  };


  const handleChangeDuraton = (e) => {
    const { name, value, valueAsNumber, type } = e.target;
    const formatedValue = type === "number" ? valueAsNumber : value;
    setDuration((v) => ({ ...v, [name]: formatedValue }));
    setInputError((err) => ({ ...err, [name]: "" }));
  };

  const handleAddEditCategory = () => {
    const valid = serviceSubCategoryValidation(
      duration,
      inputValues,
      setInputError,
    );
    if (valid) return handleOpenSnackBar("error", t("resolveTheIssues", { ns: "common" }));

    let payload = {
      categoryId: serviceCategoryId,
      image: inputValues.image,
      activeIcon: inputValues.smallIcon,
      serialNo: inputValues.sortingNum,
      type: inputValues.type,
      percentage: inputValues.profitPer,
      name: {
        en: inputValues.name_en,
        ro: inputValues.name_ro,
      },
      description: {
        en: inputValues.description_en,
        ro: inputValues.description_ro,
      },
      metaTitle: {
        en: inputValues.metaTitle_en,
        ro: inputValues.metaTitle_ro,
      },
      metaDescription: {
        en: inputValues.metaDescription_en,
        ro: inputValues.metaDescription_ro,
      },
      metaKeywords: {
        en: inputValues.metaKeywords_en,
        ro: inputValues.metaKeywords_ro,
      },
      duration: duration,
      clientQuestionnaire: questions,
      longDescription: {
        en: inputValues.longDescription_en,
        ro: inputValues.longDescription_ro,
      },
    };

    if (dialogData?.type === "add") {
      handleApiCall(createServiceSubCategory, payload, "");
    } else {
      handleApiCall(updateServiceSubCategory, payload, dialogData?.data?._id);
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      setIsLoading((prev) => ({ ...prev, save: true }));
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        dialogData.getData(searchKeys.subServiceName);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", t("somethingWentWrong"));
    } finally {
      setIsLoading((prev) => ({ ...prev, save: false }));
    }
  };


  const handleAddEditQuestion = async () => {
    if (!questionVal)
      return setInputError((err) => ({
        ...err,
        questionVal: "validation.questionRequired",
      }));

    setIsLoading((prev) => ({ ...prev, question: true }));

    try {
      const translatedText = await translateText(questionVal);

      const questionObj = {
        question: {
          en: translatedText,
          ro: questionVal,
        },
        type: "text",
      };

      setQuestions((prev) => {
        const temp = [...prev];

        if (qsType === "add") {
          temp.push(questionObj);
        }

        if (qsType === "edit") {
          temp[qsIndex] = {
            ...temp[qsIndex],
            ...questionObj,
          };
        }

        return temp;
      });

      // reset state
      setQuestionVal("");
      setQsType("add");
      setQsIndex(null);
    } catch (error) {
      handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
    } finally {
      setIsLoading((prev) => ({ ...prev, question: false }));
    }
  };

  const handleEditorChangeEnglish = (html) => {
    setInputError((err) => ({ ...err, longDescription_en: "" }));
    setInputValues((val) => ({ ...val, longDescription_en: html }));
  };

  const handleEditorChangeRomanian = (html) => {
    setInputError((err) => ({ ...err, longDescription_ro: "" }));
    setInputValues((val) => ({ ...val, longDescription_ro: html }));
  };

  const handleChangeMetaKeywords = (name, newValue) => {
    setInputValues((prev) => ({ ...prev, [name]: newValue }));
    setInputError((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTranslateToEnglish = async (key1, key2) => {
    setIsLoading((prev) => ({ ...prev, translateToEnglish: true }));
    try {
      const [englishText] = await Promise.all([
        translateText(inputValues[key1]),
      ]);
      setInputValues((prev) => ({
        ...prev,
        [key2]: englishText,
      }));
      setInputError((prev) => ({
        ...prev,
        [key2]: "",
      }));
    } catch (error) {
      handleOpenSnackBar("error", t("error.errorWhileTranslating", { ns: "common" }));
    } finally {
      setIsLoading((prev) => ({ ...prev, translateToEnglish: false }));
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading.save} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth="md"
        fullWidth
      >
        <DialogHeader
          title={dialogData?.type === "add"
            ? t("addServiceSubCategory")
            : t("editServiceSubCategory")}
          secondaryHeading={dialogData?.type === "add"
            ? t("addServiceSubCategoryInstruction")
            : t("editServiceSubCategoryInstruction")}
          onClose={handleClose}
          icon={<CategoryIcon />}
        />


        <DialogBody>
          <Grid container spacing={2}>
            {/* Large Image */}
            <Grid item xs={12}>
              <UploadBox
                inputRef={openImageFile}
                label={t("image", { ns: "common" })}
                note={t("largeImageSizeNote", { ns: "common" })}
                value={inputValues.image}
                error={inputError.image}
                loading={isLoading.image}
                placeholderImage={APP_IMAGES.uploadImage}
                accept="image/png, image/jpeg"
                height={{ xs: 200, md: 130 }}
                previewSize={{ xs: 160, md: 180 }}
                onClick={() => openImageFile.current.click()}
                onChange={(e) => handleUploadImage(e, "image")}
                onRemove={() => setInputValues({ ...inputValues, image: null })}
              />
            </Grid>
            {/* Small Icon */}
            <Grid item xs={12}>
              <UploadBox
                inputRef={openIconFile}
                label={t("smallIcon", { ns: "common" })}
                note={t("smallIconSizeNote", { ns: "common" })}
                value={inputValues.smallIcon}
                error={inputError.smallIcon}
                loading={isLoading.smallIcon}
                placeholderImage={APP_IMAGES.uploadImage}
                accept="image/png, image/jpeg"
                height={{ xs: 200, md: 130 }}
                previewSize={{ xs: 100, md: 100 }}
                onClick={() => openIconFile.current.click()}
                onChange={(e) => handleUploadImage(e, "smallIcon")}
                onRemove={() =>
                  setInputValues({ ...inputValues, smallIcon: null })
                }
              />
            </Grid>
            {/* Service Type */}
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("selectServiceType")}
                htmlFor={"service-type"}
              />
              <CustomDropDown
                name={"type"}
                disabled={isDisabled}
                value={inputValues.type}
                onChange={handleChageInputs}
                sx={useStyle.customDropDown}
                inputError={inputError.type}
                errorText={t(inputError.type)}
              >
                <MenuItem>{t("selectType")}</MenuItem>
                {serviceType?.map((val, index) => {
                  return (
                    <MenuItem
                      key={index}
                      value={val.type}
                      sx={{ textTransform: "capitalize" }}
                    >
                      {val.value}
                    </MenuItem>
                  );
                })}
              </CustomDropDown>
            </Grid>

            {/* Sorting Number */}
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("order")}
                htmlFor={"sortingNumber"}
              />
              <TextInput
                id={"sortingNumber"}
                styleProps={globalStyle.textField}
                placeholder={t("enterOrder")}
                type={"number"}
                required
                name={"sortingNum"}
                value={inputValues.sortingNum}
                onChange={handleChageInputs}
                inputError={inputError.sortingNum}
                helperText={t(inputError.sortingNum)}
              />
            </Grid>

            {/* Name in Romanian */}
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("categoryNameInRomanian")}
                htmlFor={"category-name-ro"}
              />
              <TextInput
                id={"category-name-ro"}
                placeholder={t("enterNameInRomanian")}
                required
                textLength={60}
                type={"text"}
                name={"name_ro"}
                value={inputValues.name_ro}
                onChange={handleChageInputs}
                inputError={inputError.name_ro}
                helperText={t(inputError.name_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_ro?.length}/60
                  </Typography>
                }
              />
            </Grid>

            {/* Name in English */}
            <Grid item xs={12} md={6}>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
                gap={1}
                mb={"5px"}
              >
                <Label
                  required
                  labelText={t("categoryNameInEnglish")}
                  htmlFor={"category-name-en"}
                  labelProps={{
                    marginBottom: 0,
                  }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() => handleTranslateToEnglish("name_ro", "name_en")}
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={"category-name-en"}
                placeholder={t("enterNameInEnglish")}
                required
                textLength={60}
                type={"text"}
                name={"name_en"}
                value={inputValues.name_en}
                onChange={handleChageInputs}
                inputError={inputError.name_en}
                helperText={t(inputError.name_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.name_en?.length}/60
                  </Typography>
                }
              />
            </Grid>

            {/* Description in Romanian */}
            <Grid item xs={12}>
              <Label
                required
                labelText={t("categoryDescriptionInRomanian")}
                htmlFor={"category-description-ro"}
              />
              <TextInput
                id={"category-description-ro"}
                placeholder={t("enterDescriptionInRomanian")}
                required
                textLength={255}
                type={"text"}
                name={"description_ro"}
                value={inputValues.description_ro}
                onChange={handleChageInputs}
                multiline
                rows={3}
                inputError={inputError.description_ro}
                helperText={t(inputError.description_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.description_ro?.length}/255
                  </Typography>
                }
              />
            </Grid>

            {/* Description in English */}
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t(
                    "categoryDescriptionInEnglish",
                  )}
                  htmlFor={`category-description-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("description_ro", "description_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={"category-description-en"}
                placeholder={t("enterDescriptionInEnglish")}
                required
                textLength={255}
                type={"text"}
                name={"description_en"}
                value={inputValues.description_en}
                onChange={handleChageInputs}
                multiline
                rows={3}
                inputError={inputError.description_en}
                helperText={t(inputError.description_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.description_en?.length}/255
                  </Typography>
                }
              />
            </Grid>

            {/* Meta Title */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600}>
                {t("categoryMetaInfo")}
              </Typography>
            </Grid>

            {/* Meta Title in Romanian */}
            <Grid item xs={12} md={6}>
              <Label
                required
                  labelText={t("categoryMetaTitleInRomanian")}
                htmlFor={`catrgory-meta-title-ro`}
              />
              <TextInput
                id={`catrgory-meta-title-ro`}
                required
                placeholder={t("enterMetaTitleInRomanian")}
                name={`metaTitle_ro`}
                textLength={255}
                value={inputValues.metaTitle_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaTitle_ro)}
                helperText={t(inputError.metaTitle_ro)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaTitle_ro?.length}/255
                  </Typography>
                }
              />
            </Grid>

            {/* Meta Title in English */}
            <Grid item xs={12} md={6}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t(
                    "categoryMetaTitleInEnglish",
                  )}
                  htmlFor={`catrgory-meta-title-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish("metaTitle_ro", "metaTitle_en")
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`catrgory-meta-title-en`}
                required
                placeholder={t("enterMetaTitleInEnglish")}
                name={`metaTitle_en`}
                textLength={255}
                value={inputValues.metaTitle_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaTitle_en)}
                helperText={t(inputError.metaTitle_en)}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaTitle_en?.length}/255
                  </Typography>
                }
              />
            </Grid>

            {/* Meta Description */}
            <Grid item xs={12}>
              <Label
                required
                labelText={t(
                  "categoryMetaDescriptionInRomanian",
                )}
                htmlFor={`catrgory-meta-description-ro`}
              />
              <TextInput
                id={`catrgory-meta-description-ro`}
                required
                placeholder={t(
                  "enterMetaDescriptionInRomanian",
                )}
                name={`metaDescription_ro`}
                textLength={255}
                value={inputValues.metaDescription_ro}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaDescription_ro)}
                helperText={t(inputError.metaDescription_ro)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaDescription_ro?.length}/255
                  </Typography>
                }
              />
            </Grid>

            {/* Meta Description in English */}
            <Grid item xs={12}>
              <Stack
                mb={"5px"}
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
              >
                <Label
                  required
                  labelText={t(
                    "categoryMetaDescriptionInEnglish",
                  )}
                  htmlFor={`catrgory-meta-description-en`}
                  labelProps={{ marginBottom: 0 }}
                />
                <Chip
                  label={t("translateToEnglish", { ns: "common" })}
                  size="small"
                  onClick={() =>
                    handleTranslateToEnglish(
                      "metaDescription_ro",
                      "metaDescription_en",
                    )
                  }
                  icon={
                    isLoading.translateToEnglish ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <TranslateIcon />
                    )
                  }
                  disabled={isLoading.translateToEnglish}
                />
              </Stack>
              <TextInput
                id={`catrgory-meta-description-en`}
                required
                placeholder={t(
                  "enterMetaDescriptionInEnglish",
                )}
                name={`metaDescription_en`}
                textLength={255}
                value={inputValues.metaDescription_en}
                onChange={handleChageInputs}
                inputError={Boolean(inputError.metaDescription_en)}
                helperText={t(inputError.metaDescription_en)}
                multiline
                rows={3}
                InputEndIcon={
                  <Typography variant="body2" color="text.grey">
                    {inputValues.metaDescription_en?.length}/255
                  </Typography>
                }
              />
            </Grid>

            {/* Meta Keywords in Romanian */}
            <Grid item xs={12}>
              <Label
                required
                labelText={t("categoryMetaKeywordsInRomanian")}
                htmlFor={`catrgory-meta-keywords-ro`}
              />
              <KeywordsTagInput
                placeholder={t(
                  "enterMetaKeywordsInRomanian",
                )}
                value={inputValues.metaKeywords_ro}
                onChange={(newValue) =>
                  handleChangeMetaKeywords("metaKeywords_ro", newValue)
                }
                textFieldProps={{
                  error: Boolean(inputError.metaKeywords_ro),
                  helperText: t(inputError.metaKeywords_ro),
                }}
              />
            </Grid>

            {/* Meta Keywords in English */}
            <Grid item xs={12}>
              <Label
                required
                  labelText={t("categoryMetaKeywordsInEnglish")}
                htmlFor={`catrgory-meta-keywords-en`}
              />
              <KeywordsTagInput
                placeholder={t(
                  "enterMetaKeywordsInEnglish",
                )}
                value={inputValues.metaKeywords_en}
                onChange={(newValue) =>
                  handleChangeMetaKeywords("metaKeywords_en", newValue)
                }
                textFieldProps={{
                  error: Boolean(inputError.metaKeywords_en),
                  helperText: t(inputError.metaKeywords_en),
                }}
              />
            </Grid>

            {/* Duration Info */}
            <Grid item xs={12}>
              <Typography variant="h6" fontWeight={600}>
                {t("durationInfo")}
              </Typography>
            </Grid>

            {/* Profit Percentage */}
            <Grid item xs={12} md={6}>
              <Label
                required
                labelText={t("profitPercentagePerAppointment")}
                htmlFor={"profit-percentage"}
              />
              <TextInput
                id={"profit-percentage"}
                placeholder={t("enterProfitPercentage")}
                styleProps={globalStyle.textField}
                required
                type={"number"}
                name={"profitPer"}
                onChange={handleChageInputs}
                value={inputValues.profitPer}
                inputError={inputError.profitPer}
                helperText={t(inputError.profitPer)}
                InputEndIcon={"%"}
              />
            </Grid>

            {/* Area Duration */}
            {inputValues.type === "area" && (
              <Grid item xs={6}>
                <Label
                  required
                  labelText={t("durationInMinutes")}
                  htmlFor={"category-duration-area"}
                />
                <TextInput
                  id={"category-duration-area"}
                  styleProps={globalStyle.textField}
                  placeholder={t("enterDurationInMinutes")}
                  required
                  type={"number"}
                  name={"area"}
                  value={duration?.area}
                  onChange={handleChangeDuraton}
                  inputError={inputError.area}
                  helperText={t(inputError.area)}
                  InputEndIcon={<AccessTimeIcon />}
                />
              </Grid>
            )}

            {/* Room Duration */}
            {inputValues.type === "room+area" && (
              <>
                <Grid item xs={6}>
                  <Label
                    required
                    labelText={t("roomDurationInMinutes")}
                    htmlFor={"category-duration-room"}
                  />
                  <TextInput
                    id={"category-duration-room"}
                    styleProps={globalStyle.textField}
                    placeholder={t("enterRoomDurationInMinutes")}
                    required
                    type={"number"}
                    name={"room"}
                    value={duration?.room}
                    onChange={handleChangeDuraton}
                    inputError={inputError.room}
                    helperText={t(inputError.room)}
                    InputEndIcon={<AccessTimeIcon />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Label
                    required
                    labelText={t("areaDurationInMinutes")}
                    htmlFor={"category-duration-area"}
                  />
                  <TextInput
                    id={"category-duration-area"}
                    styleProps={globalStyle.textField}
                    placeholder={t("enterAreaDurationInMinutes")}
                    required
                    type={"number"}
                    name={"area"}
                    value={duration?.area}
                    inputError={inputError.area}
                    onChange={handleChangeDuraton}
                    helperText={t(inputError.area)}
                    InputEndIcon={<AccessTimeIcon />}
                  />
                </Grid>
              </>
            )}

            {/* Fixed Duration */}
            {inputValues.type === "fixed" && (
              <Grid item xs={6}>
                <Label
                  required
                  labelText={t("durationInMinutes")}
                  htmlFor={"category-duration-fixed"}
                />
                <TextInput
                  id={"category-duration-fixed"}
                  styleProps={globalStyle.textField}
                  placeholder={t("enterDurationInMinutes")}
                  required
                  type={"number"}
                  name={"fixed"}
                  value={duration?.fixed}
                  onChange={handleChangeDuraton}
                  inputError={inputError.fixed}
                  helperText={t(inputError.fixed)}
                  InputEndIcon={<AccessTimeIcon />}
                />
              </Grid>
            )}

            {/* Interior Duration */}
            {inputValues.type === "interior+exterior" && (
              <>
                <Grid item xs={6}>
                  <Label
                    required
                    labelText={t("interiorDurationInMinutes")}
                    htmlFor={"category-duration-interior"}
                  />
                  <TextInput
                    id={"category-duration-interior"}
                    styleProps={globalStyle.textField}
                    placeholder={t("enterDurationInMinutes")}
                    required
                    type={"number"}
                    name={"interior"}
                    value={duration?.interior}
                    onChange={handleChangeDuraton}
                    inputError={inputError.interior}
                    helperText={t(inputError.interior)}
                    InputEndIcon={<AccessTimeIcon />}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Label
                    required
                    labelText={t("exteriorDurationInMinutes")}
                    htmlFor={"category-duration-exterior"}
                  />
                  <TextInput
                    id={"category-duration-exterior"}
                    styleProps={globalStyle.textField}
                    placeholder={t("enterDurationInMinutes")}
                    required
                    type={"number"}
                    name={"exterior"}
                    value={duration?.exterior}
                    onChange={handleChangeDuraton}
                    inputError={inputError.exterior}
                    helperText={t(inputError.exterior)}
                    InputEndIcon={<AccessTimeIcon />}
                  />
                </Grid>
              </>
            )}

            {/* Add Questions */}
            <Grid item xs={12}>
              <Label
                required
                labelText={t("addQuestionsInRomanian")}
                htmlFor={"category-question"}
              />
              <Stack direction={"row"} alignItems={"center"} gap={2}>
                <TextInput
                  id={"category-question"}
                  styleProps={globalStyle.textField}
                  placeholder={t("enterQuestion")}
                  required
                  value={questionVal}
                  onChange={(e) => {
                    setQuestionVal(e.target.value);
                    setInputError((err) => ({ ...err, questionVal: "" }));
                  }}
                  inputError={inputError.questionVal}
                  helperText={t(inputError.questionVal)}
                />
                <GlobalButton
                  variant={"contained"}
                  btnText={qsType === "add" ? t("add") : t("edit")}
                  handleClickBtn={handleAddEditQuestion}
                  styleProps={{
                    height: 45,
                    width: "auto",
                  }}
                />
              </Stack>

              <Stack gap={0.5} mt={2}>
                {questions?.map((val, index) => {
                  return (
                    <Stack
                      key={index}
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                      gap={1}
                      p={1}
                      bgcolor={"#f0f0f0"}
                      borderRadius={1}
                    >
                      <Box>
                        <Typography>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{"En:"}</span> {val?.question.en}
                        </Typography>
                        <Typography>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>{"RO:"}</span> {val?.question.ro}
                        </Typography>
                      </Box>
                      <Stack direction={"row"} alignItems={"center"} gap={0.3}>
                        <EditIcon
                          fontSize="xsmall"
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            const qs = questions[index];
                            setQuestionVal(qs.question.ro);
                            setQsType("edit");
                            setQsIndex(index);
                          }}
                        />
                        <DeleteIcon
                          htmlColor="red"
                          fontSize="xsmall"
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            let tempArr = [...questions];
                            tempArr.splice(index, 1);
                            setQuestions(tempArr);
                          }}
                        />
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            </Grid>

            {/* Long Description in Romanian */}
            <Grid item xs={12}>
              <Label
                required
                labelText={t("longDescriptionInRomanian")}
                htmlFor={"category-long-description-ro"}
              />
              <RichTextEditor
                value={inputValues.longDescription_ro}
                onChange={handleEditorChangeRomanian}
              />

              {inputError.longDescription_ro && (
                <FormHelperText error>
                  {t(inputError.longDescription_ro)}
                </FormHelperText>
              )}
            </Grid>

            {/* Long Description in English */}
            <Grid item xs={12}>
              <Label
                required
                labelText={t("longDescriptionInEnglish")}
                htmlFor={"category-long-description-en"}
              />

              <RichTextEditor
                value={inputValues.longDescription_en}
                onChange={handleEditorChangeEnglish}
              />

              {inputError.longDescription_en && (
                <FormHelperText error>
                  {t(inputError.longDescription_en)}
                </FormHelperText>
              )}
            </Grid>
          </Grid>
        </DialogBody>
        <DialogActionButtons
          onConfirm={handleAddEditCategory}
          onCancel={handleClose}
          confirmText={"save"}
          cancelText={"cancel"}
          isConfirmBtnDisable={isLoading.save}
          showCancelButton={true}
        />
      </BootstrapDialog>
    </>
  );
});

export default AddEditServiceSubCategoryDialog;
